// Story 8.10 (ADO-2019), DV-32/Q4: one order's review, read by the /hht-orders/{id} page. An
// order already accepted answers who accepted it and when instead (the "Already added" screen).
//
// Read while an id is given, and again whenever `reloadKey` changes (the page bumps a
// counter to re-read). A read superseded by a newer one, or by unmounting, is aborted and
// never lands. A 404 (the order is not waiting, or not the caller's) is its own state, so
// the page can return to the list.
import { useEffect, useState } from 'react';
import {
  getHhtOrderReview,
  HhtOrderProblemError,
  type HhtOrderAlreadyAccepted,
  type HhtOrderReview,
} from '../api/hht-orders.client';

export type HhtReviewState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly review: HhtOrderReview }
  | { readonly status: 'accepted'; readonly accepted: HhtOrderAlreadyAccepted }
  | { readonly status: 'notFound' }
  | { readonly status: 'error'; readonly message: string };

/** The server's Shopper-facing detail for a 404 / 409, else `fallback`. */
export function hhtProblemMessage(error: unknown, fallback: string): string {
  return error instanceof HhtOrderProblemError ? error.message : fallback;
}

/** The review of `hhtOrderId` (null while none is wanted), re-read on each `reloadKey`. */
export function useHhtReview(
  hhtOrderId: string | null,
  reloadKey: string | number,
): HhtReviewState | null {
  const [state, setState] = useState<HhtReviewState | null>(null);
  useEffect(() => {
    if (hhtOrderId === null) {
      setState(null);
      return undefined;
    }
    const controller = new AbortController();
    setState({ status: 'loading' });
    getHhtOrderReview(hhtOrderId, { signal: controller.signal }).then(
      (response) => {
        if (controller.signal.aborted) return;
        setState(
          response.status === 'ACCEPTED'
            ? { status: 'accepted', accepted: response }
            : { status: 'ready', review: response },
        );
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof HhtOrderProblemError && error.status === 404) {
          setState({ status: 'notFound' });
          return;
        }
        setState({
          status: 'error',
          message: hhtProblemMessage(error, 'Your handheld order could not be loaded.'),
        });
      },
    );
    return () => controller.abort();
  }, [hhtOrderId, reloadKey]);
  return state;
}
